import { apiClient } from '.';
import { HelpDeskResponse } from '../types/api/mypage';

// API 기본 URL
const API_BASE_URL = process.env.REACT_APP_CAMPUS_API_BASE_URL || 'https://medaicampus.kr/gateway';

// (axios 공통 클라이언트 사용으로 개별 헤더 생성 제거)

// 최신 강의 응답 타입 정의
export interface LatestLectureResponse {
  code: number;
  status: string;
  message: string;
  data: {
    lecture_info: {
      id: number;
      title: string;
      caption: string;
      thumbnail_url: string;
      tags: string[];
    };
    lecturer_info: {
      id: number;
      name: string;
      department: string;
      image_url: string;
      tags: string[];
    };
    status: string;
  } | null;
}

// 최신 강의 조회 API
export const getLatestLecture = async (): Promise<LatestLectureResponse['data']> => {
  try {
    // console.log('최신 강의 조회 요청:', {
    //   url: `${API_BASE_URL}/api/me/lectures/latest`
    // });

    const { data: result } = await apiClient.get<LatestLectureResponse>(
      '/api/me/lectures/latest',
      { withCredentials: true }
    );
    
    // console.log('최신 강의 조회 응답:', {result});

    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    return result.data;
  } catch (error) {
    /// console.error('최신 강의 조회 중 오류 발생:', error);
    throw error;
  }
};

// 수강 신청 응답 타입 정의
export interface EnrollmentResponse {
  code: number;
  status: string;
  message: string;
  data?: any;
}

// 내 강의 목록 응답 타입 정의
export interface MyLectureListResponse {
  code: number;
  status: string;
  message: string;
  data: {
    totalElements: number;
    totalPages: number;
    size: number;
    content: Array<{
      lecture_info: {
        id: number;
        title: string;
        caption: string;
        thumbnail_url: string;
        tags: string[];
      };
      lecturer_info: {
        id: number;
        name: string;
        department: string;
        image_url: string;
        tags: string[];
      };
      status: string;
      completion_rate: number;
    }>;
    number: number;
    numberOfElements: number;
    first: boolean;
    last: boolean;
    empty: boolean;
  };
}

// 진도율 응답 타입 정의
export interface ProgressResponse {
  code: number;
  status: string;
  message: string;
  data: {
    lecture_id?: number;
    lecture_title?: string;
    total_time_expected: string;
    total_time_watched: string;
    completion_rate: number;
  };
}

// 수강 신청 API
export const enrollLecture = async (lectureId: number): Promise<EnrollmentResponse> => {
  try {
    // console.log('수강 신청 API 요청:', {
    //   url: `${API_BASE_URL}/api/enrollments/enroll/${lectureId}`,
    //   lectureId
    // });

    const { data: result } = await apiClient.post<EnrollmentResponse>(
      `/api/enrollments/enroll/${lectureId}`,
      undefined,
      { withCredentials: true }
    );
    
    // console.log('수강 신청 API 응답:', {result});

    return result;
  } catch (error) {
    /// console.error('수강 신청 API 호출 중 오류:', error);
    throw error;
  }
};

// 내 강의 목록 조회 API
export const getMyLectureList = async (isDone?: boolean, page: number = 0, size: number = 100): Promise<MyLectureListResponse['data']> => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });
    
    if (isDone !== undefined) {
      params.append('isDone', isDone.toString());
    }

    // console.log('내 강의 목록 조회 요청:', {
    //   url: `${API_BASE_URL}/api/me/lectures/list?${params.toString()}`
    // });

    const { data: result } = await apiClient.get<MyLectureListResponse>(
      `/api/me/lectures/list`,
      {
        params: { page, size, ...(isDone !== undefined ? { isDone } : {}) },
        withCredentials: true,
      }
    );
    
    /// console.log('내 강의 목록 조회 응답:', {result});

    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    return result.data;
  } catch (error) {
    /// console.error('내 강의 목록 조회 중 오류 발생:', error);
    throw error;
  }
};

// 진도율 조회 API
export const getProgress = async (lectureId?: number): Promise<ProgressResponse['data']> => {
  try {
    const url = lectureId 
      ? `${API_BASE_URL}/api/me/progress?lectureId=${lectureId}`
      : `${API_BASE_URL}/api/me/progress`;

    /// console.log('진도율 조회 요청:', { url, lectureId });

    const { data: result } = await apiClient.get<ProgressResponse>(
      url.replace(API_BASE_URL, ''),
      { withCredentials: true }
    );
    
    /// console.log('진도율 조회 응답:', {result});

    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    return result.data;
  } catch (error) {
    /// console.error('진도율 조회 중 오류 발생:', error);
    throw error;
  }
};

// 출석 응답 타입 정의
export interface AttendanceResponse {
  code: number;
  status: string;
  message: string;
  data: {
    count: number;
    attendance_monthly_list: Array<{
      date: string;
      attended: boolean;
    }>;
  };
}

// 출석 목록 조회 API
export const getAttendances = async (year?: number, month?: number): Promise<AttendanceResponse['data']> => {
  try {
    const params = new URLSearchParams();
    if (year) params.append('year', year.toString());
    if (month) params.append('month', month.toString());
    
    const url = `${API_BASE_URL}/api/me/attendances${params.toString() ? `?${params.toString()}` : ''}`;
    
    /// console.log('출석 목록 조회 요청:', { url, year, month });

    const { data: result } = await apiClient.get<AttendanceResponse>(
      url.replace(API_BASE_URL, ''),
      { withCredentials: true }
    );
    
    /// console.log('출석 목록 조회 응답:', {result});

    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    return result.data;
  } catch (error) {
    /// console.error('출석 목록 조회 중 오류 발생:', error);
    throw error;
  }
};

// 학습시간 응답 타입 정의
export interface StudyTimeResponse {
  code: number;
  status: string;
  message: string;
  data: Array<{
    date: string;
    total_study_time_in_seconds: number;
  }>;
}

// 내 학습 시간 조회 API
export const getStudyTime = async (startDate?: string, endDate?: string): Promise<StudyTimeResponse['data']> => {
  try {
    const params = new URLSearchParams();
    if (startDate && endDate) {
      params.append('startDate', startDate);
      params.append('endDate', endDate);
    }
    
    const url = `${API_BASE_URL}/api/me/study-time${params.toString() ? `?${params.toString()}` : ''}`;
    
    /// console.log('학습시간 조회 요청:', { url, startDate, endDate });

    const { data: result } = await apiClient.get<StudyTimeResponse>(
      url.replace(API_BASE_URL, ''),
      { withCredentials: true }
    );
    
    /// console.log('학습시간 조회 응답:', {result});

    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    return result.data;
  } catch (error) {
    /// console.error('학습시간 조회 중 오류 발생:', error);
    throw error;
  }
};

// 나의 게시글 응답 타입 정의
export interface MyPostsResponse {
  code: number;
  status: string;
  message: string;
  data: {
    totalElements: number;
    totalPages: number;
    size: number;
    content: Array<{
      post_id: number;
      title: string;
      content: string;
      post_category: string;
      reply_cnt: number;
      view_cnt: number;
      writer_name: string;
      created_at: string;
      lecture_name: string;
      chapter: number;
      study_name: string;
      pinned: boolean;
    }>;
    number: number;
    numberOfElements: number;
    first: boolean;
    last: boolean;
    empty: boolean;
  };
}

// 나의 게시글 목록 조회 API
export const getMyPosts = async (page: number = 0, size: number = 2): Promise<MyPostsResponse['data']> => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });

    const url = `${API_BASE_URL}/api/me/posts?${params.toString()}`;
    
    /// console.log('나의 게시글 조회 요청:', { url, page, size });

    const { data: result } = await apiClient.get<MyPostsResponse>(
      url.replace(API_BASE_URL, ''),
      { withCredentials: true }
    );
    
    /// console.log('나의 게시글 조회 응답:', {result});

    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    return result.data;
  } catch (error) {
    /// console.error('나의 게시글 조회 중 오류 발생:', error);
    throw error;
  }
};

// 강의실 전용 강의 목록 API (필터링 지원)
export const getMyClassroomLectures = async (isDone?: boolean, page: number = 0, size: number = 4): Promise<MyLectureListResponse> => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString()
    });
    
    if (isDone !== undefined) {
      params.append('isDone', isDone.toString());
    }

    // console.log('강의실 강의 목록 API 호출:', {
    //   url: `${API_BASE_URL}/api/me/lectures/list?${params.toString()}`,
    //   isDone,
    //   page,
    //   size
    // });

    const { data: result } = await apiClient.get<MyLectureListResponse>(
      `/api/me/lectures/list`,
      {
        params: Object.fromEntries(params),
        withCredentials: true,
      }
    );
    /// console.log('강의실 강의 목록 API 응답:', result);
    return result;
  } catch (error) {
    /// console.error('강의실 강의 목록 API 호출 실패:', error);
    throw error;
  }
};

// 강의실 검색 API
export const searchMyClassroomLectures = async (keyword: string): Promise<{
  code: number;
  status: string;
  message: string;
  data: {
    content: Array<{
      lecture_info: {
        id: number;
        title: string;
        caption: string;
        thumbnail_url: string;
        tags: string[];
      };
      lecturer_info: {
        id: number;
        name: string;
        department: string;
        image_url: string;
        tags: string[];
      };
      status: string;
      completion_rate: number;
    }>;
    totalElements?: number;
    totalPages?: number;
    // ... 기타 페이징 관련 필드들
  };
}> => {
  try {
    const params = new URLSearchParams({
      keyword: keyword
    });

    //  console.log('강의실 검색 API 호출:', {
    //   url: `${API_BASE_URL}/api/search/my/lectures/all?${params.toString()}`,
    //   keyword
    // });

    const { data: result } = await apiClient.get(
      `/api/search/my/lectures/all`,
      {
        params: Object.fromEntries(params),
        withCredentials: true,
      }
    );
    /// console.log('강의실 검색 API 응답:', result);
    return result;
  } catch (error) {
    /// console.error('강의실 검색 API 호출 실패:', error);
    throw error;
  }
};

// 수료증 응답 타입 정의
export interface CertificateResponse {
  code: number;
  status: string;
  message: string;
  data: {
    totalElements: number;
    totalPages: number;
    size: number;
    content: Array<{
      person_certification_id: number;
      certification_id: number;
      lecture_title: string;
      template_image_url: string;
      issue_at: string;
    }>;
    number: number;
    sort: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    numberOfElements: number;
    pageable: {
      offset: number;
      sort: {
        empty: boolean;
        sorted: boolean;
        unsorted: boolean;
      };
      paged: boolean;
      pageNumber: number;
      pageSize: number;
      unpaged: boolean;
    };
    first: boolean;
    last: boolean;
    empty: boolean;
  };
}

// 내 수료증 목록 조회 API
export const getCertificates = async (page: number = 0, size: number = 4): Promise<CertificateResponse['data']> => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });

    // console.log('수료증 목록 조회 요청:', {
    //   url: `${API_BASE_URL}/api/me/certificates?${params.toString()}`
    // });

    const { data: result } = await apiClient.get<CertificateResponse>(
      `/api/me/certificates`,
      {
        params: Object.fromEntries(params),
        withCredentials: true,
      }
    );
    
    /// console.log('수료증 목록 조회 응답:', {result});

    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    return result.data;
  } catch (error) {
    /// console.error('수료증 목록 조회 중 오류 발생:', error);
    throw error;
  }
};

// 수료증 미리보기 API
export const getCertificatePreview = async (personCertificationId: number): Promise<Blob> => {
  try {
    // console.log('수료증 미리보기 요청:', {
    //   url: `${API_BASE_URL}/api/me/certificates/${personCertificationId}/preview`,
    //   personCertificationId
    // });

    const response = await apiClient.get(
      `/api/me/certificates/${personCertificationId}/preview`,
      { responseType: 'blob', withCredentials: true }
    );
    const blob = response.data as Blob;
    
    // console.log('수료증 미리보기 응답:', {
    //   response,
    //   // axios 응답 헤더는 객체 형태
    //   contentType: (response.headers as any)['content-type'],
    //   size: blob.size
    // });

    return blob;
  } catch (error) {
    /// console.error('수료증 미리보기 중 오류 발생:', error);
    throw error;
  }
};

// 수료증 발급 (다운로드) API
export const downloadCertificate = async (personCertificationId: number): Promise<Blob> => {
  try {
    //  console.log('수료증 발급 요청:', {
    //   url: `${API_BASE_URL}/api/me/certificates/${personCertificationId}/issue`,
    //   personCertificationId
    // });

    const response = await apiClient.get(
      `/api/me/certificates/${personCertificationId}/issue`,
      { responseType: 'blob', withCredentials: true }
    );
    const blob = response.data as Blob;
    
    // console.log('수료증 발급 응답:', {
    //   response,
    //   contentType: (response.headers as any)['content-type'],
    //   size: blob.size
    // });

    return blob;
  } catch (error) {
    /// console.error('수료증 발급 중 오류 발생:', error);
    throw error;
  }
};

// 내 게시글 검색 응답 타입 정의
export interface MyPostSearchResponse {
  code: number;
  status: string;
  message: string;
  data: Array<{
    id: number;
    title: string;
    category: string;
    lectureId: number;
    lectureTitle: string;
    lecturerId: number;
    lecturerName: string;
    studyCategoryName: string;
    pinned: boolean;
    exposed: boolean;
    writerName: string;
    writerProfileImageUrl: string;
    postDate: string;
    htmlcontent: string;
  }>;
}

// 내 댓글 응답 타입 정의
export interface MyRepliesResponse {
  code: number;
  status: string;
  message: string;
  data: {
    totalElements: number;
    totalPages: number;
    size: number;
    content: Array<{
      reply_id: number;
      reply_content: string;
      reply_created_at: string;
      post_id: number;
      post_title: string;
    }>;
    number: number;
    numberOfElements: number;
    first: boolean;
    last: boolean;
    empty: boolean;
  };
}

// 내 댓글 검색 응답 타입 정의
export interface MyRepliesSearchResponse {
  code: number;
  status: string;
  message: string;
  data: Array<{
    reply_id: number;
    content: string;
    writer_name: string;
    writer_profile_image_url: string;
    reply_created_at: string;
    images: string[];
  }>;
}

// 댓글단 글 응답 타입 정의
export interface MyRepliedPostsResponse {
  code: number;
  status: string;
  message: string;
  data: {
    totalElements: number;
    totalPages: number;
    size: number;
    content: Array<{
      post_id: number;
      title: string;
      content: string;
      post_category: string;
      reply_cnt: number;
      view_cnt: number;
      writer_name: string;
      created_at: string;
      lecture_name: string;
      chapter: number;
      study_name: string;
      pinned: boolean;
    }>;
    number: number;
    numberOfElements: number;
    first: boolean;
    last: boolean;
    empty: boolean;
  };
}

// 댓글단 글 검색 응답 타입 정의
export interface MyRepliedPostsSearchResponse {
  code: number;
  status: string;
  message: string;
  data: Array<{
    id: number;
    title: string;
    category: string;
    lectureId: number;
    lectureTitle: string;
    lecturerId: number;
    lecturerName: string;
    studyCategoryName: string;
    pinned: boolean;
    exposed: boolean;
    writerName: string;
    writerProfileImageUrl: string;
    postDate: string;
    htmlcontent: string;
  }>;
}

// 내 게시글 검색 API
export const searchMyPosts = async (keyword: string): Promise<MyPostSearchResponse['data']> => {
  try {
    const params = new URLSearchParams({
      keyword: keyword
    });

    //  console.log('내 게시글 검색 요청:', {
    //   url: `${API_BASE_URL}/api/search/my/posts?${params.toString()}`,
    //   keyword
    // });

    const { data: result } = await apiClient.get<MyPostSearchResponse>(
      `/api/search/my/posts`,
      {
        params: Object.fromEntries(params),
        withCredentials: true,
      }
    );
    
    /// console.log('내 게시글 검색 응답:', {result});

    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    return result.data;
  } catch (error) {
    /// console.error('내 게시글 검색 중 오류 발생:', error);
    throw error;
  }
};

// 내 댓글 목록 조회 API
export const getMyReplies = async (page: number = 0, size: number = 4): Promise<MyRepliesResponse['data']> => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });

    // console.log('내 댓글 목록 조회 요청:', {
    //   url: `${API_BASE_URL}/api/me/replies?${params.toString()}`,
    //   page, size
    // });

    const { data: result } = await apiClient.get<MyRepliesResponse>(
      `/api/me/replies`,
      {
        params: Object.fromEntries(params),
        withCredentials: true,
      }
    );
    
    /// console.log('내 댓글 목록 조회 응답:', {result});

    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    return result.data;
  } catch (error) {
    /// console.error('내 댓글 목록 조회 중 오류 발생:', error);
    throw error;
  }
};

// 내 댓글 검색 API
export const searchMyReplies = async (keyword: string): Promise<MyRepliesSearchResponse['data']> => {
  try {
    const params = new URLSearchParams({
      keyword: keyword
    });

    //  console.log('내 댓글 검색 요청:', {
    //   url: `${API_BASE_URL}/api/search/my/replies?${params.toString()}`,
    //   keyword
    // });

    const { data: result } = await apiClient.get<MyRepliesSearchResponse>(
      `/api/search/my/replies`,
      {
        params: Object.fromEntries(params),
        withCredentials: true,
      }
    );
    
    /// console.log('내 댓글 검색 응답:', {result});

    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    return result.data;
  } catch (error) {
    /// console.error('내 댓글 검색 중 오류 발생:', error);
    throw error;
  }
};

// 댓글단 글 목록 조회 API
export const getMyRepliedPosts = async (page: number = 0, size: number = 4): Promise<MyRepliedPostsResponse['data']> => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });

    //  console.log('댓글단 글 목록 조회 요청:', {
    //   url: `${API_BASE_URL}/api/me/replied-posts?${params.toString()}`,
    //   page, size
    // });

    const { data: result } = await apiClient.get<MyRepliedPostsResponse>(
      `/api/me/replied-posts`,
      {
        params: Object.fromEntries(params),
        withCredentials: true,
      }
    );
    
    /// console.log('댓글단 글 목록 조회 응답:', {result });

    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    return result.data;
  } catch (error) {
    /// console.error('댓글단 글 목록 조회 중 오류 발생:', error);
    throw error;
  }
};

// 댓글단 글 검색 API
export const searchMyRepliedPosts = async (keyword: string): Promise<MyRepliedPostsSearchResponse['data']> => {
  try {
    const params = new URLSearchParams({
      keyword: keyword
    });

    // console.log('댓글단 글 검색 요청:', {
    //   url: `${API_BASE_URL}/api/search/my/replied-posts?${params.toString()}`,
    //   keyword
    // });

    const { data: result } = await apiClient.get<MyRepliedPostsSearchResponse>(
      `/api/search/my/replied-posts`,
      {
        params: Object.fromEntries(params),
        withCredentials: true,
      }
    );
    
    /// console.log('댓글단 글 검색 응답:', {result});

    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    return result.data;
  } catch (error) {
    /// console.error('댓글단 글 검색 중 오류 발생:', error);
    throw error;
  }
};

// 문의 API /api/me/posts/help-desk

// 내 문의글 목록 조회 API
export const getMyHelpDeskPosts = async (page: number = 0, size: number = 4): Promise<HelpDeskResponse['data']> => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });

    const url = `${API_BASE_URL}/api/me/posts/help-desk?${params.toString()}`;
    
    /// console.log('내 문의글 목록 조회 요청:', { url, page, size });

    const { data: result } = await apiClient.get<HelpDeskResponse>(
      url.replace(API_BASE_URL, ''),
      { withCredentials: true }
    );
    
    /// console.log('내 문의글 목록 조회 응답:', {result});

    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    return result.data;
  } catch (error) {
    /// console.error('내 문의글 목록 조회 중 오류 발생:', error);
    throw error;
  }
};

// 마케팅 수신 동의 응답 타입 정의
export interface MarketingConsentResponse {
  code: number;
  status: string;
  message: string;
  data: {};
}

// 마케팅 수신 동의 상태 업데이트 API
export const updateMarketingConsent = async (agreed: boolean): Promise<MarketingConsentResponse['data']> => {
  try {
    //  console.log('마케팅 수신 동의 상태 업데이트 요청:', {
    //   url: `${API_BASE_URL}/api/persons/marketing-agreed?agreed=${agreed}`,
    //   agreed
    // });

    const { data: result } = await apiClient.patch<MarketingConsentResponse>(
      `/api/persons/marketing-agreed`,
      undefined,
      {
        params: { agreed },
        withCredentials: true,
      }
    );
    
    /// console.log('마케팅 수신 동의 상태 업데이트 응답:', {result});

    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    return result.data;
  } catch (error) {
    /// console.error('마케팅 수신 동의 상태 업데이트 중 오류 발생:', error);
    throw error;
  }
};

// 이수내역 미리보기 API
export const getLectureHistoryPreview = async (): Promise<Blob> => {
  try {
    //  console.log('이수내역 미리보기 요청:', {
    //   url: `${API_BASE_URL}/api/me/lectures/history/preview`
    // });

    const response = await apiClient.get(
      `/api/me/lectures/history/preview`,
      { responseType: 'blob', withCredentials: true }
    );
    const blob = response.data as Blob;
    
    //  console.log('이수내역 미리보기 응답:', {
    //   response,
    //   contentType: (response.headers as any)['content-type'],
    //   size: blob.size
    // });

    return blob;
  } catch (error) {
    /// console.error('이수내역 미리보기 중 오류 발생:', error);
    throw error;
  }
};

// 이수내역 발급 (다운로드) API
export const downloadLectureHistory = async (): Promise<Blob> => {
  try {
    // console.log('이수내역 발급 요청:', {
    //   url: `${API_BASE_URL}/api/me/lectures/history/issue`
    // });

    const response = await apiClient.get(
      `/api/me/lectures/history/issue`,
      { responseType: 'blob', withCredentials: true }
    );
    const blob = response.data as Blob;
    
    // console.log('이수내역 발급 응답:', {
    //   response,
    //   contentType: (response.headers as any)['content-type'],
    //   size: blob.size
    // });

    return blob;
  } catch (error) {
    /// console.error('이수내역 발급 중 오류 발생:', error);
    throw error;
  }
};

